import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { io, type Socket } from 'socket.io-client';
import { API_URL, useSession } from './session';

/** Guarda a conexão atual fora do React; as telas assinam com useSyncExternalStore. */
const store = {
  socket: null as Socket | null,
  listeners: new Set<() => void>(),
  set(s: Socket | null) {
    this.socket = s;
    this.listeners.forEach((l) => l());
  },
  subscribe(l: () => void) {
    store.listeners.add(l);
    return () => store.listeners.delete(l);
  },
};

/**
 * Uma conexão Socket.IO por sessão e negócio (salas `user:<id>` e `tenant:<id>` na API).
 * Mudanças de módulo recarregam o /me; o resto as telas assinam com `useRealtime`.
 */
export function RealtimeConnection() {
  const { api, status, tenantId, impersonating } = useSession();
  const queryClient = useQueryClient();
  useEffect(() => {
    if (status !== 'authenticated' || !tenantId || impersonating || import.meta.env.MODE === 'test') return;
    const s = io(API_URL || undefined, {
      transports: ['websocket'],
      // Lido a cada (re)conexão: depois de renovar o token, vale o novo.
      auth: (cb) => cb({ token: api.token(), tenantId }),
    });
    s.on('billing.entitlements_changed', () => void queryClient.invalidateQueries({ queryKey: ['me'] }));
    s.on('core.notification', () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }));
    store.set(s);
    return () => {
      s.disconnect();
      store.set(null);
    };
  }, [api, status, tenantId, impersonating, queryClient]);
  return null;
}

export function useRealtime<T>(event: string, handler: (payload: T) => void) {
  const socket = useSyncExternalStore(store.subscribe, () => store.socket);
  const ref = useRef(handler);
  useEffect(() => {
    ref.current = handler;
  });
  useEffect(() => {
    if (!socket) return;
    const fn = (p: T) => ref.current(p);
    socket.on(event, fn);
    return () => {
      socket.off(event, fn);
    };
  }, [socket, event]);
}
